import React, { Component, useEffect, Fragment, useMemo, } from 'react';
import { useToggle, useAsync, } from 'react-use';
import { last, mapValues, zip, sumBy, groupBy, keyBy, sortBy, omit, uniq, isEmpty, } from 'lodash';
import { Button, FormGroup, Label, Input } from 'reactstrap';
import Select from 'react-select';
import qs from 'qs';
import classnames from 'classnames';
import numeral from 'numeral';
import { Link } from 'react-router-dom';

import firebase, { functions } from '../../firebase';
import { integerFormat, integerChangeFormat, } from '../../util';
import { presetConsolidationJournalTypes, } from '../../shared/models/consolidationJournalType';
import { presetConsolidationAccountItems, computeCompaniesAmounts, } from '../../shared/models/consolidationAccountItem';
import { isTrialNetIncome0, } from '../../shared/models/trial';
import { accountItemCategories, accountItemCategoryNames, currencies, } from '../../shared/config';
import { prevYearMonth, } from '../../shared/util';
import { periodOfFiscalYear, } from '../../utils';
import { filterRetainedEarningConsolidationJournals, generateRowGroups, rowsForExport, computeAlerts, } from '../../shared/lib/consolidationCalculation';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import useCompaniesAmounts from '../hooks/useCompaniesAmounts';
import CompanyPage from '../hocs/CompanyPage';
import HelpLink from '../HelpLink';
import ModelFormModal from '../modals/ModelFormModal';
import CompanySyncButton from '../CompanySyncButton';
import TrialsSyncButton from '../TrialsSyncButton';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import QuerySelector from '../QuerySelector';
import QueryBoolean from '../QueryBoolean';
import ExportButton from '../ExportButton';
import WithLoading from '../WithLoading';
import Field from '../Field';
import Alerts from '../Alerts';

const { keys, entries } = Object;
const { abs } = Math;
const COLUMN_WIDTH = 120;

export default CompanyPage(function CompanyConsolidationCalculation (props) {
  const { user, company, history, location, period, yearMonth, prevYearMonths, filteredYearMonths, prevEndYearMonth, isLockedMonth, prevFiscalYear, } = props;
  const prevMonthYearMonths = useMemo(_ => filteredYearMonths.slice(0, filteredYearMonths.length - 1), [filteredYearMonths]);
  const queryParams = useQueryParams();
  const subsidiaries = useCollectionSubscription(company.ref.collection('subsidiaries').orderBy('index'));
  const { role, enabledAllRelatedCompanies, enabledRelatedCompanyIds, } = company?.users?.[user.id] || {};
  const isAdmin = user?.admin || ['owner', 'admin'].includes(role);
  const relatedCompanies = useMemo(_ => [company, ...subsidiaries].filter(_ => isAdmin || enabledAllRelatedCompanies || enabledRelatedCompanyIds?.includes(_.id)), [company, subsidiaries, isAdmin, enabledAllRelatedCompanies, enabledRelatedCompanyIds]);
  const filteredForPeriodRelatedCompanies = useMemo(
    _ => relatedCompanies.filter(_ => _.exceptPeriod == null || _.exceptPeriod >= period),
    [relatedCompanies, period]
  );
  const prevPeriod = useMemo(_ => periodOfFiscalYear(prevFiscalYear), [prevFiscalYear]);
  // 前期末時点での会社リスト（当期から除外された会社も含む）
  const filteredForPrevYearRelatedCompanies = useMemo(
    _ => relatedCompanies.filter(_ => _.exceptPeriod == null || prevPeriod == null || _.exceptPeriod >= prevPeriod),
    [relatedCompanies, prevPeriod]
  );
  const filteredRelatedCompanies = useMemo(
    _ => isEmpty(queryParams.relatedCompanyIds)
      ? filteredForPeriodRelatedCompanies
      : filteredForPeriodRelatedCompanies.filter(_ => queryParams.relatedCompanyIds.includes(_.id)),
    [filteredForPeriodRelatedCompanies, queryParams.relatedCompanyIds]
  );
  const filteredPrevYearRelatedCompanies = useMemo(
    _ => isEmpty(queryParams.relatedCompanyIds)
      ? filteredForPrevYearRelatedCompanies
      : filteredForPrevYearRelatedCompanies.filter(_ => queryParams.relatedCompanyIds.includes(_.id)),
    [filteredForPrevYearRelatedCompanies, queryParams.relatedCompanyIds]
  );
  const accountItems = useCollectionSubscription(company.ref.collection('accountItems'));
  const consolidationAccountItems = useCollectionSubscription(company.ref.collection('accountItems').where('subsidiaryId', '==', null), [company]);
  const sortedConsolidationAccountItems = useMemo(_ => sortBy(consolidationAccountItems, _ => accountItemCategoryNames.indexOf(_.account_category), 'index'), [consolidationAccountItems]);
  const allConsolidationAccountItems = useMemo(_ => [...sortedConsolidationAccountItems, ...presetConsolidationAccountItems], [sortedConsolidationAccountItems]);
  const allConsolidationAccountItemsById = useMemo(_ => keyBy(allConsolidationAccountItems, 'id'), [allConsolidationAccountItems]);
  const disclosureSettingRef = company.ref.collection('disclosureSettings').doc(yearMonth);
  const disclosureSetting = useDocumentSubscription(disclosureSettingRef, [yearMonth]);
  const exchangeRate = useDocumentSubscription(company.ref.collection('exchangeRates').doc(yearMonth), [yearMonth]);
  const showsInForeignCurrency = queryParams.showsInForeignCurrency === '1';
  const foreignCurrency = queryParams.foreignCurrency;
  const foreignCurrencyOptions = useMemo(_ => uniq([...subsidiaries.map(_ => _.currency), ...keys(exchangeRate || {}).filter(_ => /-(cr|ar|mar)$/.test(_)).map(_ => _.replace(/-(cr|ar|mar)$/, ''))]).filter(_ => _ && _ !== 'jpy').map(_ => ({ label: currencies[_]?.label || _.toUpperCase(), value: _, })), [subsidiaries, exchangeRate]);
  const rates = useMemo(_ => {
    if(!showsInForeignCurrency || !foreignCurrency || !exchangeRate) return null;
    const cr = exchangeRate[`${foreignCurrency}-cr`];
    const ar = exchangeRate[`${foreignCurrency}-ar`];
    return { cr: cr || null, ar: ar || null };
  }, [showsInForeignCurrency, foreignCurrency, exchangeRate]);
  const rateForCategory = (accountItemCategory) => {
    if(!rates) return 1;
    if(!accountItemCategory) return rates.cr;
    if(['pl', 'cr'].includes(accountItemCategory.type)) return rates.ar;
    return rates.cr;
  };
  const formatAmount = (value, accountItemCategory) => {
    if(!showsInForeignCurrency) return integerFormat(value);
    const rate = rateForCategory(accountItemCategory);
    if(!rate) return '-';
    return numeral(value / rate).format('0,0.00');
  };
  const formatChange = (value, accountItemCategory) => {
    if(!showsInForeignCurrency) return integerChangeFormat(value);
    const rate = rateForCategory(accountItemCategory);
    if(!rate) return '-';
    return numeral(value / rate).format('+0,0.00');
  };
  const convertForExport = (value, accountItemCategory) => {
    if(!showsInForeignCurrency || !foreignCurrency) return value;
    const rate = rateForCategory(accountItemCategory);
    if(!rate) return null;
    return value / rate;
  };
  const consolidationJournalTypes = useCollectionSubscription(company.ref.collection('consolidationJournalTypes'), [company]);
  const sortedConsolidationJournalTypes = useMemo(_ => sortBy(consolidationJournalTypes, 'index'), [consolidationJournalTypes]);
  const allConsolidationJournalTypes = useMemo(_ => [...presetConsolidationJournalTypes, ...sortedConsolidationJournalTypes], [sortedConsolidationJournalTypes]);
  const consolidationJournalTypesById = useMemo(_ => keyBy(allConsolidationJournalTypes, 'id'), [allConsolidationJournalTypes]);
  const consolidationJournalTypeOptions = allConsolidationJournalTypes.map(_ => ({ label: _.name, value: _.id, }));
  const filteredConsolidationJournalTypes = useMemo(_ => isEmpty(queryParams.consolidationJournalTypeIds) ? allConsolidationJournalTypes : allConsolidationJournalTypes.filter(_ => queryParams.consolidationJournalTypeIds.includes(_.id)), [queryParams.consolidationJournalTypeIds, allConsolidationJournalTypes]);
  const relatedCompanyOptions = (relatedCompanies || []).map(_ => ({ label: _.display_name, value: _.id }));

  const { items: prevMonthItems, } = useCompaniesAmounts(company, filteredRelatedCompanies, filteredConsolidationJournalTypes, allConsolidationAccountItems, accountItems, prevMonthYearMonths, true);
  const { items, monthGroups, prevMonthGroup, } = useCompaniesAmounts(company, filteredRelatedCompanies, filteredConsolidationJournalTypes, allConsolidationAccountItems, accountItems, filteredYearMonths, true);
  const { items: prevYearItems, monthGroups: prevYearMonthGroups, prevMonthGroup: prevYearPrevMonthGroup, } = useCompaniesAmounts(company, filteredPrevYearRelatedCompanies, filteredConsolidationJournalTypes, allConsolidationAccountItems, accountItems, prevYearMonths, true);
  const retainedEarningItems = useMemo(_ => (
    [monthGroups, prevYearMonthGroups].every(_ => !isEmpty(_)) ? (
      computeCompaniesAmounts(
        company,
        filteredRelatedCompanies,
        filteredConsolidationJournalTypes,
        allConsolidationAccountItems,
        accountItems,
        // NOTE: 当期の仕訳のisOpeningを使う
        (prevYearMonthGroups || []).map((_, i) => ({ ..._, consolidationJournals: filterRetainedEarningConsolidationJournals(allConsolidationAccountItemsById, last(monthGroups)?.consolidationJournals || []), })),
        (prevYearPrevMonthGroup != null ? { ...prevYearPrevMonthGroup, consolidationJournals: filterRetainedEarningConsolidationJournals(allConsolidationAccountItemsById, prevMonthGroup?.consolidationJournals || []), } : null),
      )
    ) : null
  ), [monthGroups, prevMonthGroup, prevYearMonthGroups, prevYearPrevMonthGroup]);

  const displayConsolidationJournalTypes = uniq((items || []).flatMap(_ => _.consolidationColumns.filter(_ => abs(_.amount) > 0).map(_ => _.type)));
  const [rowGroups, retainedEarningRowGroups] = useMemo(_ => [items, prevMonthItems || prevYearItems, retainedEarningItems].every(_ => _) && generateRowGroups(items || [], prevMonthItems || prevYearItems, retainedEarningItems || [], queryParams.showsAll === '1'), [items, prevMonthItems, prevYearItems, retainedEarningItems, queryParams.showsAll]) || [];
  const alerts = computeAlerts(items || [], retainedEarningItems || [], prevYearItems || [], company);
  useEffect(() => {
    if(disclosureSetting === null) {
      disclosureSettingRef.set({ createdAt: new Date(), }, { merge: true });
    }
  }, [disclosureSetting]);

  return props.translate(
    <div className="company-account-item-mappings container-fluid">
      <div className="bg-white mt-4">
        <div className="d-flex justify-content-end mb-3">
          <HelpLink text="連結精算表を確認する" />
        </div>
        <div className="d-flex justify-content-center mb-3">
          <h4>連結精算表</h4>
        </div>
        <WithLoading loading={!(rowGroups && retainedEarningRowGroups)}>
          <div className="position-relative">
            <Alerts alerts={alerts} />
            <div className="mb-2 d-flex align-items-end justify-content-start gap-1">
              <QuerySelector simple paramName="relatedCompanyIds" width={300} isMulti options={relatedCompanyOptions} label="会社" />
              <QuerySelector simple paramName="consolidationJournalTypeIds" width={300} isMulti options={consolidationJournalTypeOptions} label="仕訳種別" />
              <QueryBoolean paramName="showsAll" label="金額なしの行も表示" defaultValue={'0'} />
              <QueryBoolean paramName="showsInForeignCurrency" label="外貨で表示" defaultValue={'0'} />
              {
                showsInForeignCurrency && (
                  <QuerySelector simple paramName="foreignCurrency" width={150} options={foreignCurrencyOptions} label="通貨" />
                )
              }
              <ExportButton fileName={queryParams.filename} screenName={showsInForeignCurrency && foreignCurrency ? `連結精算表_${foreignCurrency.toUpperCase()}` : '連結精算表'} rows={rowsForExport(company, items, prevMonthItems || prevYearItems, retainedEarningItems, displayConsolidationJournalTypes, consolidationJournalTypesById, queryParams.showsAll === '1', convertForExport)} encoding={queryParams.encoding} />
            </div>
            {
              !isEmpty(queryParams.relatedCompanyIds) && <div className="alert alert-info">
                開始・連結仕訳は、選択された会社のみを含む仕訳だけを表示しています
              </div>
            }
            {
              showsInForeignCurrency && foreignCurrency && (
                <div className="alert alert-info">
                  外貨表示モードでは、円建ての金額を当月の為替レートで割って{(currencies[foreignCurrency]?.label || foreignCurrency.toUpperCase())}建てに換算して表示しています。BS（資産・負債・純資産）はCR、PL・製造原価はARで割っています。
                  {rates && (!rates.cr || !rates.ar) && (
                    <div className="mt-1 text-danger">
                      {!rates.cr && 'CRが未設定です。'}{!rates.ar && 'ARが未設定です。'}該当箇所は「-」で表示されます。
                    </div>
                  )}
                </div>
              )
            }
            {
              rowGroups && retainedEarningRowGroups && (
                <div className="overflow-auto position-relative" style={{ maxHeight: '100vh', zIndex: 0, }}>
                  <table className="table table-bordered sticky-table m-0">
                    <thead className="thead-light text-center">
                      <tr>
                        <th style={{ minWidth: 230 }} rowSpan={2} className="sticky">連結科目</th>
                        {
                          filteredRelatedCompanies.map((company) => {
                            return (
                              <th key={company.id} style={{ minWidth: COLUMN_WIDTH, maxWidth: COLUMN_WIDTH, }} rowSpan={2}>
                                {company.display_name || company.name}
                              </th>
                            );
                          })
                        }
                        <th style={{ minWidth: COLUMN_WIDTH }} rowSpan={2}>
                          単純合算
                        </th>
                        {
                          displayConsolidationJournalTypes.length > 0 && (
                            <th style={{ minWidth: COLUMN_WIDTH * displayConsolidationJournalTypes.length }} colSpan={displayConsolidationJournalTypes.length}>
                              開始・連結仕訳
                            </th>
                          )
                        }
                        <th style={{ minWidth: COLUMN_WIDTH }} rowSpan={2}>
                          連結FS
                        </th>
                        <th style={{ minWidth: COLUMN_WIDTH }} rowSpan={2}>
                          比率
                        </th>
                        <th style={{ minWidth: COLUMN_WIDTH * 2, }} colSpan={2}>
                          開示
                        </th>
                        <th style={{ minWidth: COLUMN_WIDTH }} rowSpan={2}>
                          対前月差額
                        </th>
                      </tr>
                      <tr>
                        {
                          filteredConsolidationJournalTypes.filter(_ => displayConsolidationJournalTypes.includes(_.id)).map((consolidationJournalType) => {
                            const { id, name } = consolidationJournalType;
                            return (
                              <th key={id} style={{ minWidth: COLUMN_WIDTH }}>
                                {name}
                              </th>
                            );
                          })
                        }
                        <th style={{ minWidth: COLUMN_WIDTH }}>
                          開示科目
                        </th>
                        <th style={{ minWidth: COLUMN_WIDTH }}>
                          その他
                        </th>
                      </tr>
                    </thead>
                    {
                      (rowGroups || []).map(({ type, rowGroups }) => {
                        return (
                          <Fragment key={type}>
                            <thead className="text-center">
                              <tr>
                                <th className="bg-light">
                                  {type.toUpperCase()}
                                </th>
                              </tr>
                            </thead>
                            <tbody className="thead-light">
                              {
                                rowGroups.map(({ category, rows, }) => {
                                  return (
                                    <Fragment key={category.name}>
                                      {
                                        rows.map(({ item, itemType, accountItemCategory, companyColumns, simpleSum, consolidationColumns, conclusiveAmount, ratio, prevMonthChange, }) => {
                                          const disclosesAsOther = itemType === 'consolidationAccountItem' && disclosureSetting?.otherDiscloseItems?.includes(item.group_name);
                                          return (
                                            <tr key={item.id} className={itemType === 'category' && 'font-weight-bold'}>
                                              <th style={{ textIndent: (category.indent() + (itemType === 'category' ? 0 : 1)) * 18 }} className={classnames('sticky', { 'font-weight-normal': itemType !== 'category' })}>
                                                {item.name}
                                              </th>
                                              {
                                                companyColumns.map((companyColumn) => {
                                                  const { company, exchangedAmount } = companyColumn;
                                                  return (
                                                    <td key={company.id} className="text-right">
                                                      {formatAmount(exchangedAmount, accountItemCategory)}
                                                    </td>
                                                  );
                                                })
                                              }
                                              <td className="text-right">
                                                {formatAmount(simpleSum, accountItemCategory)}
                                              </td>
                                              {
                                                consolidationColumns.filter(_ => displayConsolidationJournalTypes.includes(_.type)).map((consolidationColumn) => {
                                                  const { type, amount } = consolidationColumn;
                                                  const Component = itemType === 'category' ? 'span' : Link;
                                                  return (
                                                    <td className="text-right" key={type}>
                                                      <Component to={`/companies/${company.id}/consolidationJournals/?${qs.stringify({ relatedCompanyIds: queryParams.relatedCompanyIds, period, yearMonth, types: [type], consolidationAccountItemIds: [item.id], onlyFilteredRows: 1, })}`}>
                                                        {formatAmount(amount, accountItemCategory)}
                                                      </Component>
                                                    </td>
                                                  )
                                                })
                                              }
                                              <td className="text-right">
                                                {formatAmount(conclusiveAmount, accountItemCategory)}
                                              </td>
                                              <td className="text-right">
                                                {isFinite(ratio) ? numeral(ratio).format('0.00%') : '-'}
                                              </td>
                                              <td className="text-right">
                                                {item.group_name}
                                              </td>
                                              <td>
                                                {
                                                  itemType === 'consolidationAccountItem' && (
                                                    <FormGroup className="m-0">
                                                      <FormGroup check>
                                                        <Label check>
                                                          <Input type="checkbox" checked={disclosesAsOther === true} onChange={_ => disclosureSetting?.ref.update({ otherDiscloseItems: firebase.firestore.FieldValue[_.target.checked ? 'arrayUnion' : 'arrayRemove'](item.group_name) })} disabled={isLockedMonth} />
                                                          その他
                                                        </Label>
                                                      </FormGroup>
                                                    </FormGroup>
                                                  )
                                                }
                                              </td>
                                              <td className="text-right">
                                                {formatChange(prevMonthChange, accountItemCategory)}
                                              </td>
                                            </tr>
                                          );
                                        })
                                      }
                                    </Fragment>
                                  );
                                })
                              }
                            </tbody>
                          </Fragment>
                        );
                      })
                    }
                    <thead className="text-center">
                      <tr>
                        <th className="bg-light">
                          利益剰余金計算書
                        </th>
                      </tr>
                    </thead>
                    <tbody className="thead-light">
                      {
                        (retainedEarningRowGroups || []).map(({ category, rows, }) => {
                          return (
                            <Fragment key={category.name}>
                              {
                                rows.map(([item, prevYearItem]) => {
                                  const { itemType, accountItemCategory, consolidationColumns, conclusiveAmount, ratio, } = item;
                                  return (
                                    <tr key={item.item.id} className={itemType === 'category' && 'font-weight-bold'}>
                                      <th style={{ textIndent: (category.indent() + (itemType === 'category' ? 0 : 1)) * 18 }} className={classnames('sticky', { 'font-weight-normal': itemType !== 'category' })}>
                                        {item.item.name}
                                      </th>
                                      {
                                        (() => {
                                          const prevYearCompanyColumnsByCompanyId = keyBy(prevYearItem.companyColumns, _ => _.company.id);
                                          return item.companyColumns.map((companyColumn) => {
                                          const prevYearCompanyColumn = prevYearCompanyColumnsByCompanyId[companyColumn.company.id] || { exchangedAmount: 0 };
                                          return (
                                            <td key={companyColumn.company.id} className="text-right text-nowrap">
                                              <div>
                                                {formatAmount(prevYearCompanyColumn.exchangedAmount, accountItemCategory)}
                                              </div>
                                              <div>
                                                {formatChange(companyColumn.exchangedAmount - prevYearCompanyColumn.exchangedAmount, accountItemCategory)}
                                              </div>
                                              <div>
                                                {formatAmount(companyColumn.exchangedAmount, accountItemCategory)}
                                              </div>
                                            </td>
                                          );
                                        });
                                        })()
                                      }
                                      <td className="text-right text-nowrap">
                                        <div>
                                          {formatAmount(prevYearItem.simpleSum, accountItemCategory)}
                                        </div>
                                        <div>
                                          {formatChange(item.simpleSum - prevYearItem.simpleSum, accountItemCategory)}
                                        </div>
                                        <div>
                                          {formatAmount(item.simpleSum, accountItemCategory)}
                                        </div>
                                      </td>
                                      {
                                        item.consolidationColumns.filter(_ => displayConsolidationJournalTypes.includes(_.type)).map((consolidationColumn, i) => {
                                          const { type, } = consolidationColumn;
                                          const prevYearConsolidationColumn = prevYearItem.consolidationColumns.filter(_ => displayConsolidationJournalTypes.includes(_.type))[i];
                                          return (
                                            <td className="text-right text-nowrap" key={type}>
                                              <div>
                                                {formatAmount(prevYearConsolidationColumn.amount, accountItemCategory)}
                                              </div>
                                              <div>
                                                {formatChange(consolidationColumn.amount - prevYearConsolidationColumn.amount, accountItemCategory)}
                                              </div>
                                              <div>
                                                {formatAmount(consolidationColumn.amount, accountItemCategory)}
                                              </div>
                                            </td>
                                          )
                                        })
                                      }
                                      <td className="text-right text-nowrap">
                                        <div>
                                          {formatAmount(prevYearItem.conclusiveAmount, accountItemCategory)}
                                        </div>
                                        <div>
                                          {formatChange(item.conclusiveAmount - prevYearItem.conclusiveAmount, accountItemCategory)}
                                        </div>
                                        <div>
                                          {formatAmount(item.conclusiveAmount, accountItemCategory)}
                                        </div>
                                      </td>
                                      <td className="text-right">
                                        -
                                      </td>
                                      <td className="text-right">
                                        -
                                      </td>
                                      <td className="text-right">
                                        -
                                      </td>
                                      <td className="text-right">
                                        -
                                      </td>
                                    </tr>
                                  );
                                })
                              }
                            </Fragment>
                          );
                        })
                      }
                    </tbody>
                  </table>
                </div>
              )
            }
          </div>
        </WithLoading>
      </div>
    </div>
  );
});
